import { useMomentaryStore } from "@/stores/momentary";
import { myAxios, myAxiosRetry } from "@/services/myAxios";

// Инициализируем хранилища Pinia
const getStores = () => {
    return {
        momentaryStore: useMomentaryStore()
    };
};

// Хелпер для API-запросов
const makeApiRequest = async (url, method = 'get', data = null, customAxios = myAxios) => {
    const { momentaryStore } = getStores();

    momentaryStore.setBusy(true);
    momentaryStore.setAPIError(false);

    try {
        const response = method === 'get'
            ? await customAxios.get(url)
            : await customAxios.post(url, data);

        momentaryStore.setBusy(false);
        return response.data;
    } catch (error) {
        momentaryStore.setBusy(false);
        momentaryStore.setAPIError(true);
        throw error;
    }
};


// Получение и инициализация ECDIS (POST /init, картинка по /render)
export async function getECDISData(initialPosition = null) {
    // Используем переданные параметры или дефолтные
    const params = initialPosition || {
        lat: 59.98,
        lon: 29.70,
        depth: 1,
        sog: 20,
        cog: 80
    };
    
    console.log('Initializing ECDIS with position:', params);
    
    try {
        // Инициализация через внешний API (POST /init)
        const initResponse = await fetch('https://mkdemo.bgeo.fi/init', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params)
        });
        
        if (!initResponse.ok) {
            throw new Error(`Init request failed: ${initResponse.status}`);
        }
        
        console.log('ECDIS initialization successful with params:', params);
        
        // Возвращаем URL для получения картинки и координаты
        return {
            imageUrl: 'https://mkdemo.bgeo.fi/render',
            scale: calculateScale(10), // Дефолтный zoom
            coordinates: {
                lat: params.lat,
                lon: params.lon
            }
        };
    } catch (error) {
        console.error("Failed to initialize ECDIS:", error);
        return {
            imageUrl: '',
            scale: '',
            coordinates: {
                lat: params.lat,
                lon: params.lon
            }
        };
    }
}

// Получение обновленной картинки ECDIS
export async function refreshECDISImage() {
    try {
        // Добавляем timestamp чтобы избежать кеширования
        const timestamp = new Date().getTime();
        return {
            imageUrl: `https://mkdemo.bgeo.fi/render?t=${timestamp}`,
            timestamp: timestamp
        };
    } catch (error) {
        console.error("Failed to refresh ECDIS image:", error);
        return {
            imageUrl: '',
            timestamp: 0
        };
    }
}

// Обновление позиции корабля без переинициализации карты
export async function updateShipPosition(position) {
    try {
        console.log('Updating ship position:', position);
        
        const response = await fetch('https://mkdemo.bgeo.fi/updateship', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                lat: position.lat,
                lon: position.lon,
                sog: position.sog,
                cog: position.cog
            })
        });
        
        if (!response.ok) {
            throw new Error(`Update ship position failed: ${response.status}`);
        }
        
        console.log('Ship position updated successfully');
        return true;
    } catch (error) {
        console.error("Failed to update ship position:", error);
        return false;
    }
}

// Отправка команды управления ECDIS
export async function sendECDISCommand(command) {
    try {
        const url = `https://mkdemo.bgeo.fi/${command}`;
        console.log('Sending ECDIS command:', command);
        
        const response = await fetch(url, {
            method: 'GET'
        });
        
        if (!response.ok) {
            throw new Error(`Command failed: ${response.status}`);
        }
        
        console.log('ECDIS command executed successfully:', command);
        return true;
    } catch (error) {
        console.error(`Failed to execute ECDIS command ${command}:`, error);
        return false;
    }
}

// Отправка команды движения карты
export async function moveECDISMap(direction) {
    const commandMap = {
        'up': 'up',
        'down': 'down', 
        'left': 'left',
        'right': 'right'
    };
    
    const command = commandMap[direction];
    if (!command) {
        console.error('Unknown movement direction:', direction);
        return false;
    }
    
    return await sendECDISCommand(command);
}

// Команды управления поворотом (yaw)
export async function rotateECDISMap(direction) {
    const commandMap = {
        'clockwise': 'yawCW',
        'counterclockwise': 'yawCCW'
    };
    
    const command = commandMap[direction];
    if (!command) {
        console.error('Unknown rotation direction:', direction);
        return false;
    }
    
    return await sendECDISCommand(command);
}

// Команды управления наклоном (pitch)
export async function tiltECDISMap(direction) {
    const commandMap = {
        'in': 'pitchIn',
        'out': 'pitchOut'
    };
    
    const command = commandMap[direction];
    if (!command) {
        console.error('Unknown tilt direction:', direction);
        return false;
    }
    
    return await sendECDISCommand(command);
}

// Команды управления масштабом (zoom)
export async function zoomECDISMap(direction) {
    const commandMap = {
        'in': 'zoomIn',
        'out': 'zoomOut'
    };
    
    const command = commandMap[direction];
    if (!command) {
        console.error('Unknown zoom direction:', direction);
        return false;
    }
    
    return await sendECDISCommand(command);
}

// Центрирование карты на корабле
export async function centerECDISOnShip(deviceId) {
    try {
        // TODO: Заменить на реальный API endpoint когда серверная часть будет готова
        const data = await makeApiRequest(`api/v1/navigation/center/${deviceId}`, 'post');
        
        // Пока возвращаем тестовые данные
        return {
            imageUrl: 'ecdis.png',
            scale: '1:50000',
            coordinates: {
                lat: 59.9139,
                lon: 10.7522
            }
        };
    } catch (error) {
        console.error("Failed to center ECDIS map:", error);
        return null;
    }
}

// Сброс всех настроек ECDIS
export async function resetECDISSettings(deviceId) {
    try {
        // TODO: Заменить на реальный API endpoint когда серверная часть будет готова
        const data = await makeApiRequest(`api/v1/navigation/reset/${deviceId}`, 'post');
        
        // Возвращаем данные с дефолтными настройками
        return {
            imageUrl: 'ecdis.png',
            scale: '1:50000',
            coordinates: {
                lat: 59.9139,
                lon: 10.7522
            },
            settings: {
                tilt: 0,
                rotation: 0,
                zoom: 10,
                is3DEnabled: false,
                offsetX: 0,
                offsetY: 0
            }
        };
    } catch (error) {
        console.error("Failed to reset ECDIS settings:", error);
        return null;
    }
}

// Вспомогательная функция для расчета масштаба на основе зума
function calculateScale(zoomLevel) {
    // Примерная формула для расчета масштаба на основе уровня зума
    const baseScale = 50000;
    const scale = Math.round(baseScale / zoomLevel);
    return `1:${scale}`;
}
